/**
 * PATH: apps/ai/src/pages/reports/ReportComponents.tsx
 * PURPOSE: Sub-components for Reports page (TemplateCard, ParameterForm, GenerationProgress)
 */

import {
  FileText, Clock, Loader2, CheckCircle, AlertTriangle, Download, ExternalLink,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import type { ReportTemplate, ParameterConfig, GeneratedReport } from './types';
import { REPORT_TEMPLATES } from './types';

export function TemplateCard({ template, selected, onClick }: { template: ReportTemplate; selected: boolean; onClick: () => void }) {
  const Icon = template.icon;
  return (
    <button onClick={onClick} className={cn("group relative p-5 rounded-xl border text-left transition-all duration-200 hover:shadow-lg hover:border-primary/30", selected ? "border-primary bg-primary/5 ring-2 ring-primary/20" : "border-border bg-card")}>
      <div className="flex items-start gap-4">
        <div className={cn("p-3 rounded-xl bg-gradient-to-br text-white shadow-lg group-hover:scale-110 transition-transform duration-200", template.color)}><Icon className="w-6 h-6" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors">{template.name}</h3>
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{template.description}</p>
          <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{template.estimatedTime}</span>
            <span className="px-2 py-0.5 rounded-full bg-accent text-foreground font-medium capitalize">{template.category}</span>
          </div>
        </div>
      </div>
      {selected && <div className="absolute top-3 right-3"><CheckCircle className="w-5 h-5 text-primary" /></div>}
    </button>
  );
}

export function ParameterForm({ parameters, values, onChange }: { parameters: ParameterConfig[]; values: Record<string, string>; onChange: (id: string, value: string) => void }) {
  return (
    <div className="space-y-4">
      {parameters.map((param) => (
        <div key={param.id}>
          <label className="block text-sm font-medium text-foreground mb-1.5">{param.label}{param.required && <span className="text-red-500 ml-1">*</span>}</label>
          {param.type === 'select' && (
            <select value={values[param.id] || param.defaultValue || ''} onChange={(e) => onChange(param.id, e.target.value)} className={cn("w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50")}>
              {param.options?.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          )}
          {param.type === 'text' && <input type="text" value={values[param.id] || ''} onChange={(e) => onChange(param.id, e.target.value)} className={cn("w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50")} />}
        </div>
      ))}
    </div>
  );
}

export function GenerationProgress({ report }: { report: GeneratedReport }) {
  const template = REPORT_TEMPLATES.find(t => t.id === report.templateId);
  return (
    <div className={cn("p-4 rounded-xl border", report.status === 'completed' ? "border-emerald-500/30 bg-emerald-500/5" : report.status === 'failed' ? "border-red-500/30 bg-red-500/5" : "border-border bg-card")}>
      <div className="flex items-center gap-3">
        <div className={cn("p-2 rounded-lg", report.status === 'completed' ? "bg-emerald-100 dark:bg-emerald-900/50" : report.status === 'failed' ? "bg-red-100 dark:bg-red-900/50" : "bg-accent")}>
          {report.status === 'generating' ? <Loader2 className="w-5 h-5 text-primary animate-spin" /> : report.status === 'completed' ? <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /> : <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />}
        </div>
        <div className="flex-1 min-w-0"><p className="text-sm font-medium text-foreground">{report.name}</p><p className="text-xs text-muted-foreground capitalize">{report.status}</p></div>
        {report.status === 'completed' && report.downloadUrl && <a href={report.downloadUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"><Download className="w-4 h-4" />Download</a>}
        {report.status === 'completed' && report.qdriveUrl && <a href={report.qdriveUrl} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-600 hover:bg-emerald-500/10 rounded-lg transition-colors"><ExternalLink className="w-4 h-4" />QDrive</a>}
      </div>
      {report.status === 'generating' && report.progress !== undefined && <div className="mt-3"><div className="h-1.5 rounded-full bg-accent overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-300" style={{ width: `${report.progress}%` }} /></div><p className="text-xs text-muted-foreground mt-1.5">{report.progress}% complete</p></div>}
    </div>
  );
}
